/**
 * Gear for the slots added with the bag lane (docs/handoff/newdir/ITEMS.md): กางเกง, รองเท้า, อาวุธลับ, and the two
 * special slots สมบัติ (a treasure, its passive in its stats) and ตำราสกิล (a manual: +damage with one art's moves,
 * through `art` as a rolled legend's bonus). None of these has a look on the figure (`visual: ''`): the character
 * models are final, so they give stats only. Data only; `items.ts` lists them after its own.
 */
import type { ItemDef } from './items';

export const MORE_GEAR: readonly ItemDef[] = [
  // ── กางเกง ──
  {
    id: 'cotton-trousers',
    name: 'กางเกงผ้าฝ้ายรัดแข้ง',
    slot: 'legs',
    rarity: 'common',
    desc: 'กางเกงผ้าฝ้ายเนื้อหนา รัดแข้งด้วยผ้าพันกันกิ่งไม้เกี่ยว',
    stats: { def: 2, maxHp: 8 },
    visual: '',
    worth: 60,
  },
  {
    id: 'wolf-greaves',
    name: 'สนับแข้งหนังหมาป่า',
    slot: 'legs',
    rarity: 'rare',
    desc: 'สนับแข้งหนังหมาป่าเย็บสองชั้น เตะได้หนักโดยไม่เจ็บหน้าแข้ง',
    stats: { def: 4, maxHp: 20 },
    visual: '',
    worth: 280,
  },
  // ── รองเท้า ──
  {
    id: 'straw-sandals',
    name: 'รองเท้าฟางสาน',
    slot: 'feet',
    rarity: 'common',
    desc: 'รองเท้าฟางสานแน่น เบาเท้า เดินทางไกลไม่พอง',
    stats: { speed: 0.02 },
    visual: '',
    worth: 40,
  },
  {
    id: 'cloud-boots',
    name: 'รองเท้าเหยียบเมฆ',
    slot: 'feet',
    rarity: 'rare',
    desc: 'รองเท้าผ้าดำพื้นบาง ปักลายเมฆที่ข้อ ผู้ฝึกวิชาตัวเบาชอบสวม',
    stats: { def: 1, speed: 0.05 },
    visual: '',
    worth: 320,
  },
  // ── อาวุธลับ: thrown from their own slot (data/hiddenWeapons.ts) ──
  {
    id: 'iron-darts',
    name: 'ลูกดอกเหล็กเจ็ดดอก',
    slot: 'hidden',
    rarity: 'common',
    desc: 'ลูกดอกเหล็กปลายแหลมเจ็ดดอกในถุงหนังข้างเอว ซัดสองดอกใส่ศัตรูในระยะ 7 เมตร',
    stats: { crit: 0.01 },
    visual: '',
    throwSkill: 'hidden-darts',
    worth: 80,
  },
  {
    id: 'plum-needles',
    name: 'เข็มดอกเหมย',
    slot: 'hidden',
    rarity: 'rare',
    desc: 'เข็มเงินบางเฉียบซ่อนในแขนเสื้อ ซัดทีเดียวสี่เล่มใส่ศัตรูในระยะ 8 เมตร',
    stats: { crit: 0.02, atk: 1 },
    visual: '',
    throwSkill: 'hidden-needles',
    worth: 360,
  },
  // ── สมบัติ: a passive, carried ──
  {
    id: 'jade-amulet',
    name: 'หยกพกพิทักษ์กาย',
    slot: 'treasure',
    rarity: 'rare',
    desc: 'หยกเขียวสลักอักษรคุ้มภัย ผูกเชือกแดงติดตัว ว่ากันว่ากันคมดาบได้หนึ่งครั้งในร้อย',
    stats: { maxHp: 40, def: 2 },
    visual: '',
    worth: 300,
  },
  {
    id: 'tiger-tally',
    name: 'ป้ายหยกเสือ',
    slot: 'treasure',
    rarity: 'epic',
    desc: 'ป้ายสั่งทัพรูปเสือครึ่งซีก ผู้ถือรู้สึกถึงไอสังหารของนายทัพเก่า ตีแรงขึ้นและดูดเลือดคืนเล็กน้อย',
    stats: { atk: 4, lifesteal: 0.02 },
    visual: '',
    worth: 600,
  },
  // ── ตำราสกิล: +damage with one art's moves ──
  {
    id: 'manual-nine-swords',
    name: 'ตำราเพลงกระบี่ร่องศิลาฉบับคัดลอก',
    slot: 'manual',
    rarity: 'rare',
    desc: 'ตำราเย็บสันด้วยด้ายแดง มีบันทึกของผู้ฝึกรุ่นก่อนข้างหน้ากระดาษ อ่านแล้วกระบวนท่าของเพลงกระบี่ร่องศิลาแรงขึ้น 12%',
    stats: {},
    art: { id: 'nine-swords', pct: 0.12 },
    visual: '',
    worth: 260,
  },
  {
    id: 'manual-cloud-palm',
    name: 'ตำราฝ่ามือเมฆาคล้อยฉบับคัดลอก',
    slot: 'manual',
    rarity: 'rare',
    desc: 'ตำราฝ่ามือที่มีภาพวาดการเดินลมปราณทุกหน้า อ่านแล้วกระบวนท่าของฝ่ามือเมฆาคล้อยแรงขึ้น 12%',
    stats: {},
    art: { id: 'cloud-palm', pct: 0.12 },
    visual: '',
    worth: 260,
  },
];

/**
 * แหวน and หยกห้อย: the two stat-only accessory slots of items v2 (docs/systems/ITEMS_V2.md §1.2). A ring's implicit is
 * แม่นยำ / แม่นยำภายใน, a pendant's ลมปราณสูงสุด; the rest is random affixes (data/affixes.ts). No look on the
 * figure (`visual: ''`). Data only; `items.ts` lists them after its own.
 */
export const ACCESSORIES: readonly ItemDef[] = [
  // ── แหวน ──
  {
    id: 'bronze-ring',
    name: 'แหวนทองเหลืองสลักลาย',
    slot: 'ring',
    rarity: 'common',
    desc: 'แหวนทองเหลืองสลักลายเมฆ ช่างในตลาดทำขายให้คนเดินทาง',
    stats: { hit: 2 },
    visual: '',
    worth: 60,
  },
  {
    id: 'jade-ring',
    name: 'แหวนหยกเขียวสงบจิต',
    slot: 'ring',
    rarity: 'rare',
    desc: 'แหวนหยกเขียวเนื้อใส สวมแล้วลมปราณเดินเรียบ ปล่อยพลังภายในแม่นขึ้น',
    stats: { magicHit: 5 },
    visual: '',
    worth: 300,
  },
  // ── หยกห้อย ──
  {
    id: 'plain-pendant',
    name: 'หยกห้อยเอวสีขาว',
    slot: 'pendant',
    rarity: 'common',
    desc: 'หยกขาวห้อยเอวผูกเชือกแดง ของขวัญจากบ้านที่จากมา',
    stats: { maxQi: 8 },
    visual: '',
    worth: 60,
  },
  {
    id: 'cloud-pendant',
    name: 'หยกห้อยลายเมฆาวน',
    slot: 'pendant',
    rarity: 'epic',
    desc: 'หยกห้อยสลักลายเมฆวนรอบอักษรโบราณ กล่าวกันว่าเก็บกำลังภายในได้มากกว่าหยกธรรมดา',
    stats: { maxQi: 24 },
    visual: '',
    worth: 520,
  },
];
